import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  LuGraduationCap, LuPlus, LuPencil, LuCopy, LuSend, LuArchive, LuTrash2, LuSearch, LuCheck,
} from 'react-icons/lu';
import TrainingCourseEditor from './TrainingCourseEditor';
import { COURSE_STATUS, Badge, Modal, errorMessage } from './trainingUtils';
import './training.css';

/**
 * Treinamentos — área gerencial (exclusiva da DHR Consultoria / is_super_admin).
 * Listagem de cursos com busca e filtro de status; o editor do curso abre
 * nesta mesma página (sem nova entrada de menu), seguindo o padrão
 * `currentComponent` do App.js.
 */

function NewCourseModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!title.trim()) { setError('Informe o título do curso.'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await api.post('/training/courses', { title: title.trim() });
      onCreated(data);
    } catch (e) {
      setError(errorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal title="Novo curso" icon={<LuGraduationCap color="var(--accent)" />} onClose={onClose} maxWidth={460}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div>
        <label className="form-label">Título do curso *</label>
        <input className="form-control" value={title} autoFocus maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Ex.: Técnicas de negociação em cobrança" />
        <div className="training-hint">O curso nasce como rascunho. Você completa os dados e o conteúdo em seguida.</div>
      </div>
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleCreate} disabled={saving}>
          <LuCheck /> {saving ? 'Criando…' : 'Criar curso'}
        </button>
      </div>
    </Modal>
  );
}

export default function TrainingCoursesPage() {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [issues, setIssues] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/training/courses', { params: { search: search || undefined, status: status || undefined } });
      setCourses(data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar os cursos'));
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3500); };

  const runAction = async (fn, okMsg) => {
    setError(''); setIssues(null);
    try {
      await fn();
      flash(okMsg);
      load();
    } catch (e) {
      setError(errorMessage(e, 'Não foi possível concluir a ação'));
      if (e.response?.data?.issues) setIssues(e.response.data.issues);
    }
  };

  const handleDuplicate = (c) => runAction(() => api.post(`/training/courses/${c.id}/duplicate`), `"${c.title}" duplicado como rascunho.`);
  const handlePublish = (c) => runAction(() => api.post(`/training/courses/${c.id}/publish`), `"${c.title}" publicado.`);
  const handleArchive = (c) => {
    if (!window.confirm(`Arquivar "${c.title}"? Ele deixa de aparecer para novos alunos; quem já está matriculado mantém o histórico.`)) return;
    runAction(() => api.post(`/training/courses/${c.id}/archive`), `"${c.title}" arquivado.`);
  };
  const handleDelete = (c) => {
    if (!window.confirm(`Excluir definitivamente "${c.title}"? Essa ação não pode ser desfeita.`)) return;
    runAction(() => api.delete(`/training/courses/${c.id}`), `"${c.title}" excluído.`);
  };

  if (editingId) {
    return <TrainingCourseEditor courseId={editingId} onBack={() => { setEditingId(null); load(); }} />;
  }

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Treinamentos</div>
          <div className="page-card-subtitle">Catálogo de cursos da DHR Consultoria liberados para as empresas clientes</div>
        </div>
        <button type="button" className="training-btn training-btn--primary" onClick={() => setShowNew(true)}>
          <LuPlus /> Novo curso
        </button>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}
      {issues && (
        <div className="training-issues">
          <strong>O que falta para publicar:</strong>
          <ul>{issues.map((i) => <li key={i}>{i}</li>)}</ul>
        </div>
      )}
      {success && <div className="alert-custom success">{success}</div>}

      <div className="training-toolbar">
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <LuSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="form-control" style={{ paddingLeft: 32, maxWidth: 'none' }} placeholder="Buscar por título ou descrição"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {Object.entries(COURSE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading && !courses.length && <div className="training-hint">Carregando…</div>}

      {!loading && !courses.length && (
        <div className="empty-state">
          <LuGraduationCap size={36} color="var(--text-3)" style={{ marginBottom: 8 }} />
          <p>{search || status ? 'Nenhum curso encontrado com esses filtros' : 'Nenhum curso cadastrado ainda'}</p>
          {!search && !status && <small>Clique em "Novo curso" para montar o primeiro.</small>}
        </div>
      )}

      <div className="training-course-grid">
        {courses.map((c) => (
          <div key={c.id} className="training-course-card">
            <div className="training-course-cover" style={c.cover_image ? { backgroundImage: `url("${c.cover_image}")` } : undefined}>
              {!c.cover_image && <LuGraduationCap />}
            </div>
            <div className="training-course-body">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Badge meta={COURSE_STATUS[c.status]} />
                {c.category && <Badge meta={{ label: c.category, bg: 'var(--info-soft)', color: 'var(--info)' }} />}
              </div>
              <div className="training-course-title" onClick={() => setEditingId(c.id)}>{c.title}</div>
              <div className="training-course-meta">
                <span>{c.module_count} módulo{c.module_count !== 1 ? 's' : ''}</span>
                <span>{c.lesson_count} aula{c.lesson_count !== 1 ? 's' : ''}</span>
                <span>{c.assessment_count} avaliaç{c.assessment_count !== 1 ? 'ões' : 'ão'}</span>
                {c.workload_hours !== null && <span>{Number(c.workload_hours).toLocaleString('pt-BR')} h</span>}
              </div>
              <div className="training-course-meta">
                <span>{c.release_count} empresa{c.release_count !== 1 ? 's' : ''} liberada{c.release_count !== 1 ? 's' : ''}</span>
                <span>{c.enrollment_count} aluno{c.enrollment_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="training-course-actions">
              <button type="button" className="training-btn training-btn--sm training-btn--dark" onClick={() => setEditingId(c.id)}>
                <LuPencil /> Editar
              </button>
              <button type="button" className="training-btn training-btn--sm" onClick={() => handleDuplicate(c)} title="Duplicar">
                <LuCopy /> Duplicar
              </button>
              {c.status !== 'published' && (
                <button type="button" className="training-btn training-btn--sm" onClick={() => handlePublish(c)}>
                  <LuSend /> Publicar
                </button>
              )}
              {c.status === 'published' && (
                <button type="button" className="training-btn training-btn--sm" onClick={() => handleArchive(c)}>
                  <LuArchive /> Arquivar
                </button>
              )}
              {c.enrollment_count === 0 && (
                <button type="button" className="training-btn training-btn--sm training-btn--danger" onClick={() => handleDelete(c)} title="Excluir">
                  <LuTrash2 />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <NewCourseModal
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setShowNew(false); setEditingId(c.id); }}
        />
      )}
    </div>
  );
}
